/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Copy, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Banner, Button, Card, CardHeader, Input, Skeleton, useConfirm } from './ui';

// The API may live on another origin than the app (REACT_APP_API_URL).
export const calendarURL = (path, baseURL = api?.defaults?.baseURL) => {
  try {
    return new URL(path, new URL(baseURL || '/', window.location.origin)).toString();
  } catch (e) {
    return path;
  }
};

// Private ICS subscription: ex-dividend and earnings dates of the held
// lines, ECB meetings, the PEA's fifth anniversary and the deposit day.
const CalendarSubscription = () => {
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/calendar');
      setUrl(calendarURL(res.data.path));
    } catch (err) {
      setError(err?.response?.data?.error || 'Calendrier indisponible.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié', { description: 'Collez-le dans « Ajouter un calendrier par URL ».' });
    } catch (e) {
      setError('Copie impossible : sélectionnez le lien à la main.');
    }
  };

  const rotate = async () => {
    const ok = await confirm({
      title: 'Renouveler le lien du calendrier ?',
      message: 'L’ancien lien cessera de fonctionner : il faudra réabonner vos agendas.',
      confirmLabel: 'Renouveler',
      cancelLabel: 'Annuler',
    });
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/calendar/rotate');
      setUrl(calendarURL(res.data.path));
      toast.success('Nouveau lien créé');
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de renouveler le lien.');
    } finally {
      setBusy(false);
    }
  };

  const webcal = url.replace(/^https?:/, 'webcal:');
  return (
    <Card data-testid="calendar-subscription">
      <CardHeader
        icon={CalendarDays}
        title="Calendrier"
        description="Abonnez votre agenda : détachements de dividendes et publications de résultats de vos lignes, réunions de la BCE, cinq ans du PEA et jour de versement. Mis à jour toutes les six heures environ, sans aucun montant."
      />
      {error && <Banner tone="loss" role="alert" className="mb-4">{error}</Banner>}
      {!url && !error ? (
        <div role="status"><span className="sr-only">Chargement…</span><Skeleton className="h-11 w-full" /></div>
      ) : url && (
        <div className="space-y-4">
          <Input aria-label="Lien d’abonnement au calendrier" readOnly value={url} onFocus={(e) => e.target.select()} inputClassName="text-sm" />
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" leadingIcon={Copy} onClick={copy}>Copier le lien</Button>
            <Button as="a" href={webcal} variant="secondary" size="sm">Ouvrir dans Calendrier (Apple)</Button>
            <Button as="a" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`} target="_blank" rel="noreferrer noopener" variant="secondary" size="sm">Ajouter à Google Agenda</Button>
            <Button variant="ghost" size="sm" leadingIcon={RefreshCw} loading={busy} onClick={rotate}>Renouveler le lien</Button>
          </div>
          <p className="text-xs leading-relaxed text-fg-subtle">Ce lien est privé : quiconque le possède voit vos dates (jamais vos montants). Renouvelez-le s’il a circulé.</p>
        </div>
      )}
      {confirmDialog}
    </Card>
  );
};

export default CalendarSubscription;
